
import React, { JSX, useEffect, useRef, useState } from 'react'

export const SCOREBUG_COVER_MS = 300
export const SCOREBUG_HOLD_MS = 300
export const SCOREBUG_AWAY_MS = 300

export const SCOREBUG_EASE = 'cubic-bezier(0.33, 0, 0.67, 1)'

export const ScoreCelebration = ({
  images,
  cornerRadius = 0,
  onReveal,
  onDone
}: {
  images: string[]
  cornerRadius?: number
  onReveal?: () => void
  onDone: () => void
}): JSX.Element => {
  const [phase, setPhase] = useState<'enter' | 'cover' | 'leave'>('enter')

  useEffect(() => {
    let cancelled = false
    let raf = 0
    const begin = (): void => {
      if (cancelled) return
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => { if (!cancelled) setPhase('cover') })
      })
    }
    const decoded = Promise.all(images.map(async (src) => {
      const img = new Image()
      img.src = src
      return await img.decode().catch(() => undefined)
    }))
    const budget = new Promise((resolve) => globalThis.setTimeout(resolve, 400))
    void Promise.race([decoded, budget]).then(begin, begin)
    return () => { cancelled = true; cancelAnimationFrame(raf) }
  }, [images])

  useEffect(() => {
    if (phase !== 'cover') return
    const t = globalThis.setTimeout(() => setPhase('leave'), SCOREBUG_COVER_MS + SCOREBUG_HOLD_MS)
    return () => globalThis.clearTimeout(t)
  }, [phase])

  const revealedRef = useRef(false)
  useEffect(() => {
    if (phase === 'leave' && !revealedRef.current) {
      revealedRef.current = true
      onReveal?.()
    }
  }, [phase, onReveal])

  useEffect(() => {
    if (phase !== 'leave') return
    const t = globalThis.setTimeout(onDone, SCOREBUG_AWAY_MS)
    return () => globalThis.clearTimeout(t)
  }, [phase, onDone])

  const translateY = phase === 'enter' ? '100%' : phase === 'cover' ? '0%' : '-100%'
  const durationMs = phase === 'leave' ? SCOREBUG_AWAY_MS : SCOREBUG_COVER_MS
  const sliceWidth = images.length <= 1 ? '100%' : `${100 / images.length}%`

  return (
    <div
      aria-hidden='true'
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        overflow: 'hidden',
        pointerEvents: 'none',
        borderRadius: `${cornerRadius}px`
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          transform: `translateY(${translateY})`,
          transition: `transform ${durationMs}ms ${SCOREBUG_EASE}`
        }}
      >
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=''
            style={{ width: sliceWidth, height: '100%', objectFit: 'cover' }}
          />
        ))}
      </div>
    </div>
  )
}
